// composables/commerce/useProductSuggestion.js
//
// The seam between the product-level suggestion DOOR (which lives on a post
// card, deep inside a virtualized list) and the SHEET it opens (which cannot
// live there).
//
// ── WHY THE DOOR CANNOT SIMPLY HOST ITS OWN SHEET ───────────────────────────
//
// A Framework7 sheet is `position: fixed`. VirtualPostList positions every row
// with `transform: translateY(...)`, and a transformed element becomes the
// CONTAINING BLOCK for fixed-position descendants (CSS Transforms 1, "fixed
// positioning containing block") — so a sheet mounted inside a card would be
// trapped inside that row's box rather than covering the viewport, and would
// scroll away with it. Nothing throws; it just renders wrong.
//
// A per-card instance would also be wrong on its own terms: there are dozens of
// cards on screen and one sheet is ever open.
//
// ── SO: A SHARED OPEN REQUEST, AND ONE HOST PER PAGE ────────────────────────
//
// Module-level state, deliberately — this is one shared request ("open the
// product sheet for THIS product"), not per-component state. Any card calls
// `open()`; the single `<product-suggestion-host>` a page mounts OUTSIDE its
// transformed rows renders the sheet. That is one mount point per page instead
// of prop-drilling an event through VirtualPostList's default slot and into
// two pages that wire none of PostCard's other emits either.
//
// `postId` rides along for one reason: the 200 hands back the PRODUCT, and a
// post carries the union of its products' tips. Without knowing which post she
// tapped from, the card behind the sheet would keep showing no receipt until
// the next feed read.
import { readonly, shallowReactive } from 'vue';

// shallowReactive, not reactive: `product` is a pinia-orm model instance, and
// deep-wrapping an ORM record in a second proxy is how identity checks against
// the repo's own objects start failing. Only the three top-level keys need to
// be reactive.
const state = shallowReactive({
  opened: false,
  product: null,
  postId: null,
});

export function useProductSuggestion() {
  /**
   * Ask the page's host to open the sheet for this product.
   *
   * @param {object} product the Product model or resource — must carry `id`,
   *   and carries `mySuggestion` when she has already named a shop for it.
   * @param {string|number|null} postId the card she tapped from, so the receipt
   *   can paint there straight away.
   */
  function open(product, postId = null) {
    if (product?.id == null) return;
    state.product = product;
    state.postId = postId == null ? null : String(postId);
    state.opened = true;
  }

  /**
   * Closed, and the target released. Clearing `product` matters: this state
   * outlives any one page, and a stale product left behind would seed the next
   * open with the previous one's shop for the instant before the watcher runs.
   */
  function close() {
    state.opened = false;
    state.product = null;
    state.postId = null;
  }

  /**
   * The host's `@update:opened` handler. Only the CLOSING half is honoured —
   * the sheet may close itself (backdrop, swipe, a 403 it hands back), but
   * nothing opens it except `open()`, which is the call that says WHICH product.
   * An `opened = true` arriving from anywhere else would show a sheet with no
   * target.
   */
  function setOpened(value) {
    if (value !== true) close();
  }

  // readonly: the door and the host READ this; only the two functions above
  // write it. Shared mutable state that anyone can poke is how "which product
  // is the sheet for" stops having one answer.
  return { state: readonly(state), open, close, setOpened };
}
